/**
 * MQTT-based WebRTC signaling — browser connects directly to EMQX.
 *
 * Flow:
 * 1. Connect to EMQX via MQTT over WebSocket (mqttToken auth)
 * 2. Subscribe to signaling/response (answers + ICE from DeviceAgent)
 * 3. Publish signaling/request (offer + ICE to DeviceAgent)
 * 4. Disconnect MQTT after WebRTC is established (zero persistent cost)
 */
import mqtt from "mqtt";
import { createViewerId } from "./id.js";
const MQTT_CONNECT_TIMEOUT_MS = 10_000;
export class MQTTSignaling {
    client = null;
    params;
    events;
    requestTopic;
    responseTopic;
    viewerId;
    /** Per-connect correlation id (Stage-1 timelines). Optional on wire. */
    traceId = "";
    constructor(params, events, viewerId) {
        this.params = params;
        this.events = events;
        this.requestTopic = `${params.deviceTopic}/signaling/request`;
        this.responseTopic = `${params.deviceTopic}/signaling/response`;
        // Caller (WebRTCClient) pins a stable viewerId across reconnects
        // so the bridge's grace-window + ICE restart fast-path can match
        // the returning viewer. A per-construction UUID would force a
        // full scrcpy rebuild on every reconnect.
        this.viewerId = viewerId ?? createViewerId();
    }
    setTraceId(traceId) {
        this.traceId = traceId;
    }
    async connect() {
        return new Promise((resolve, reject) => {
            // WebRTCClient owns reconnects because every full retry must first
            // re-read the latest stream params and MQTT token. Letting mqtt.js
            // run its own reconnect loop would keep retrying the original token
            // and can overlap with WebRTCClient tearing down/rebuilding a session.
            this.client = mqtt.connect(this.params.mqttUrl, {
                username: "viewer",
                password: this.params.mqttToken,
                protocolVersion: 5,
                clean: true,
                reconnectPeriod: 0,
                resubscribe: false,
                connectTimeout: MQTT_CONNECT_TIMEOUT_MS,
                keepalive: 30,
            });
            let resolvedOnce = false;
            this.client.on("connect", () => {
                // mqtt.js 的 CONNACK 可能在 disconnect() 同步置空 this.client
                // 之后才被派发（已入队的事件逃过 removeAllListeners 的窗口极小，
                // 但非 0），此时 subscribe 会在 null 上爆。所有 on(...) 回调都
                // 显式判空做深度防御，和 disconnect() 里的 removeAllListeners
                // 构成双保险。
                if (!this.client || resolvedOnce)
                    return;
                this.client.subscribe(this.responseTopic, (err) => {
                    if (err) {
                        if (!resolvedOnce) {
                            resolvedOnce = true;
                            this.events.onError(err);
                            reject(err);
                        }
                        return;
                    }
                    resolvedOnce = true;
                    resolve();
                });
            });
            this.client.on("reconnect", () => {
                if (!this.client)
                    return;
                // Defensive breadcrumb: reconnectPeriod=0 means mqtt.js must never
                // own a retry. If a future dependency change violates that contract,
                // make it visible without leaking connection credentials.
                console.warn("[MQTTSignaling] unexpected broker reconnect event");
            });
            this.client.on("offline", () => {
                if (!this.client)
                    return;
                console.warn("[MQTTSignaling] broker offline — WebRTC recovery will rebuild signaling");
            });
            this.client.on("message", (_topic, payload) => {
                if (!this.client)
                    return;
                try {
                    const msg = JSON.parse(payload.toString());
                    if (msg.type === "viewer_kicked" && msg.viewerId === this.viewerId) {
                        this.events.onViewerKicked?.(msg.reason ?? "replaced by another viewer");
                        return;
                    }
                    if (msg.viewerId && msg.viewerId !== this.viewerId)
                        return;
                    if (msg.type === "answer" && msg.sdp) {
                        console.info(`[MQTTSignaling] event=client.answer_received viewer=${this.viewerId.slice(0, 8)} ` +
                            `trace_id=${this.traceId} sdp_bytes=${String(msg.sdp).length}`);
                        this.events.onAnswer(msg.sdp);
                    }
                    else if (msg.type === "ice" && msg.candidate) {
                        this.events.onIceCandidate(msg.candidate);
                    }
                }
                catch {
                    // ignore malformed messages
                }
            });
            this.client.on("error", (err) => {
                if (!this.client)
                    return;
                // Reject the initial connect once. WebRTCClient immediately force-
                // closes this client and schedules a fresh session with refreshed
                // params; mqtt.js itself never retries this socket.
                if (!resolvedOnce) {
                    resolvedOnce = true;
                    this.events.onError(err);
                    reject(err);
                }
                else {
                    console.warn("[MQTTSignaling] broker error:", err.message);
                }
            });
        });
    }
    /** Whether the underlying MQTT client currently has a live session. */
    isConnected() {
        return this.client?.connected === true;
    }
    sendOffer(sdp, opts) {
        if (!this.client?.connected)
            return;
        const payload = {
            type: "offer",
            sdp,
            viewerId: this.viewerId,
        };
        if (this.traceId)
            payload.traceId = this.traceId;
        if (opts?.iceRestart)
            payload.iceRestart = true;
        console.info(`[MQTTSignaling] event=client.offer_sent viewer=${this.viewerId.slice(0, 8)} ` +
            `trace_id=${this.traceId} ice_restart=${Boolean(opts?.iceRestart)} sdp_bytes=${sdp.length}`);
        this.client.publish(this.requestTopic, JSON.stringify(payload));
    }
    sendIceCandidate(candidate) {
        if (!this.client?.connected)
            return;
        this.client.publish(this.requestTopic, JSON.stringify({
            type: "ice",
            candidate: candidate.toJSON(),
            viewerId: this.viewerId,
        }));
    }
    /**
     * Publish an explicit "I'm going away" Close signal. The bridge
     * uses this to arm its session-hold grace window immediately
     * instead of waiting for ICE timeout (~10-30 s). MUST only be
     * called from intentional teardown paths (component unmount,
     * explicit disconnect) — NOT from reconnect-in-progress paths,
     * because the browser is about to re-offer under the same
     * viewerId and we don't want to pay the grace round-trip.
     */
    sendClose(reason) {
        if (!this.client?.connected)
            return;
        this.client.publish(this.requestTopic, JSON.stringify({ type: "close", reason, viewerId: this.viewerId }));
    }
    /**
     * Tear down the MQTT connection.
     *
     * `graceful = false` (default) closes the socket with `force=true`,
     * dropping any in-flight publishes. This is what we want on reconnect
     * paths where we're about to come back under the same viewerId — the
     * bridge's ICE-disconnected path will arm grace regardless.
     *
     * `graceful = true` allows the client to drain queued publishes
     * (needed when we just called `sendClose`) before closing.
     */
    disconnect(opts) {
        if (!this.client)
            return;
        const client = this.client;
        // 先置 null，任何已入队但还没执行的 on(...) 回调都会被深度防御
        // 守卫挡住。再 removeAllListeners 把未来可能再触发的事件彻底断掉
        // （包括 mqtt.js 在 end 之后仍会派发的 CONNACK / close 之类），
        // 最后才 end 真正的 socket。这个顺序杜绝了 "client.end 后 CONNACK
        // 到了，回调里引用空 this.client 报 TypeError" 这类竞态。
        this.client = null;
        client.removeAllListeners();
        client.end(opts?.graceful !== true);
    }
}
